import { useConnection } from "@/stores/connection";
import { useSession } from "@/stores/session";
import { API_ROOT } from "./endpoints";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
  /** The request never reached the server (offline, DNS, dropped connection). */
  get isNetwork() {
    return this.status === 0;
  }
}

type Options = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Don't raise the session-expired sheet on 401 (sign-in pages). */
  quiet401?: boolean;
};

/**
 * Headers sent with every request from this page. The staff app's phone view
 * (inside the Plan's iframe) sets `X-KK-App: preview`, so the server reads
 * its preview session instead of the manager's own.
 */
const sticky: Record<string, string> = {};
export function setStickyHeader(name: string, value: string | null) {
  if (value === null) delete sticky[name];
  else sticky[name] = value;
}

/** `path` comes from `endpoints` — never a literal at the call site. */
export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_ROOT}${path}`, {
      method: opts.method ?? "GET",
      credentials: "include",
      headers: { ...sticky, ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}), ...opts.headers },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    useConnection.getState().markOffline();
    throw new ApiError(0, "offline", "No connection. It will be sent when the signal returns.");
  }

  useConnection.getState().markContact();

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    const code = err?.code ?? "http_" + res.status;
    if (res.status === 401 && !opts.quiet401 && (code === "session_expired" || code === "unauthenticated")) {
      useSession.getState().expire();
    }
    throw new ApiError(res.status, code, err?.message ?? "Something went wrong. Try again.", err?.details);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : "";
};

/** A 422's problems by field path ("name", "rooms.0.name"), or null for any other failure. */
export function fieldErrors(e: unknown): Record<string, string> | null {
  if (!(e instanceof ApiError) || e.code !== "validation" || !Array.isArray(e.details)) return null;
  const out: Record<string, string> = {};
  for (const d of e.details as { path?: unknown; message?: unknown }[]) {
    if (typeof d.path === "string" && typeof d.message === "string" && !(d.path in out)) out[d.path] = d.message;
  }
  return out;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}

export function newKey(): string {
  // crypto.randomUUID only exists in a secure context, and the desk is served
  // over plain http. getRandomValues is always there, so mint the v4 by hand
  // rather than throw somewhere a booking can be lost.
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (n) => n.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Send a file as raw bytes (photos, receipts). `path` comes from `endpoints`;
 * the file's type rides in Content-Type and its name in the query.
 */
export async function upload<T>(path: string, file: Blob, params: Record<string, string | undefined> = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_ROOT}${path}${qs(params)}`, { method: "POST", credentials: "include", headers: { ...sticky, "Content-Type": file.type }, body: file });
  } catch {
    useConnection.getState().markOffline();
    throw new ApiError(0, "offline", "No connection. Add the photo again when the signal returns.");
  }
  useConnection.getState().markContact();
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    if (res.status === 401) useSession.getState().expire();
    throw new ApiError(res.status, err?.code ?? "http_" + res.status, err?.message ?? "The file couldn't be sent. Try again.", err?.details);
  }
  return data as T;
}

/** Where to load a stored file from (a FileDTO's `url`). */
export const fileSrc = (url: string) => `${API_ROOT}${url}`;
